import * as React from 'react'

import { Box, Chevron, Text, Touchable } from '@factorialco/deprecated-design-system'
import reverse from 'lodash/reverse'
import sortBy from 'lodash/sortBy'
import moment from 'moment'

import BoxList from 'modules/core/components/BoxList'
import Loading from 'modules/core/components/Loading'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { Other } from 'modules/timeoff/components/Leaves/LeavesList/Section/Item'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import { FACTORIAL_CREATION_YEAR } from 'modules/timeoff/lib/constants'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import { Leaves } from 'modules/timeoff/store/collections/leaves'
import type LeaveModel from 'modules/timeoff/store/models/Leave'

interface Props {
  employeeId: string
  year: number
  employeeTimeoffPolicyId: string | null | undefined
}

const PastLeaves = ({ employeeId, year, employeeTimeoffPolicyId }: Props) => {
  const [show, setShow] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const { startsOn } = useContractsVersion()
  const direction = show ? 'up' : 'down'
  const pastLeaves: Leaves = React.useRef(new Leaves()).current
  const onClick = () => setShow(!show)

  const fetchData = React.useCallback(async () => {
    setLoading(true)

    const yearHired = (startsOn && startsOn.year()) || FACTORIAL_CREATION_YEAR
    await pastLeaves.fetch({
      data: {
        employee_id: employeeId,
        terminated: true,
        from: dateToString(moment([yearHired, 0, 1]), 'backend'),
        to: dateToString(moment([year - 1, 11, 31]), 'backend'),
      },
    })
    setLoading(false)
  }, [employeeId, pastLeaves, year])

  // Fetches past absences whenever `show` is toggled to true, or the user
  // updates a leave (fetchCount changes)
  const currentAndFutureLeavesContextValue = useLeaves()
  React.useEffect(() => {
    show && fetchData()
  }, [show, fetchData, currentAndFutureLeavesContextValue.fetchCount])

  const buttonCopy = () => {
    return show ? i18n.t('time_off.past.hide') : i18n.t('time_off.past.show')
  }

  const earlierLeaves = (): LeaveModel[] => {
    const beforeThisYear = pastLeaves.filter((leave) => {
      const finishYear = moment(leave.attributes.finish_on).year()

      return isNaN(finishYear) || finishYear < year
    })

    return reverse(sortBy(beforeThisYear, (l: LeaveModel) => l.attributes.finish_on))
  }

  return (
    <>
      <Touchable onClick={onClick}>
        <Box
          paddingY="s16"
          background="grey000White"
          flexDirection="row"
          alignItems="center"
          gap="s8"
          flexGrow
        >
          <Text size="300" color="grey1000" weight="medium">
            {buttonCopy()}
          </Text>
          <Chevron type="primary" direction={direction} />
        </Box>
      </Touchable>
      {loading && <Loading />}
      {!loading && show && (
        <>
          {pastLeaves.length !== 0 && (
            <BoxList>
              {earlierLeaves().map((l) => (
                <Other
                  key={l.attributes.id}
                  leave={l}
                  employeeId={employeeId}
                  employeeTimeoffPolicyId={employeeTimeoffPolicyId}
                />
              ))}
            </BoxList>
          )}
          {(!pastLeaves || pastLeaves.length === 0) && (
            <Text.Normal>{i18n.t('time_off.past.no_earlier_absences')}</Text.Normal>
          )}
        </>
      )}
    </>
  )
}

export default PastLeaves
